'use client';
import React from 'react';
import Link from 'next/link';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Users,
  Package,
  ShieldAlert,
} from 'lucide-react';

interface KPICardProps {
  title: string;
  value: string;
  subValue?: string;
  change?: string;
  changePositive?: boolean;
  changeLabel?: string;
  icon: React.ReactNode;
  accentColor?: string;
  accentBg?: string;
  alert?: boolean;
  children?: React.ReactNode;
  span2?: boolean;
  href?: string;
}

function KPICard({
  title,
  value,
  subValue,
  change,
  changePositive,
  changeLabel,
  icon,
  accentColor,
  accentBg,
  alert,
  children,
  span2,
  href,
}: KPICardProps) {
  const cardContent = (
    <div
      className={`relative rounded-xl p-4 sm:p-5 flex flex-col gap-3 transition-all duration-200 hover:brightness-110 ${href ? 'cursor-pointer hover:scale-[1.01]' : ''}`}
      style={{
        backgroundColor: alert ? 'rgba(153,27,27,0.18)' : 'var(--dark-card)',
        border: `1px solid ${alert ? 'rgba(220,38,38,0.35)' : 'var(--dark-border)'}`,
      }}
    >
      {alert && (
        <div
          className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse"
          style={{ backgroundColor: 'var(--danger-text)' }}
        />
      )}
      {href && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: 'var(--dark-muted)' }}>
            <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: 'var(--dark-muted)', letterSpacing: '0.08em' }}
          >
            {title}
          </p>
          <p
            className="text-3xl font-bold mt-1 tabular-nums"
            style={{ color: alert ? '#fca5a5' : accentColor ?? 'var(--dark-text)' }}
          >
            {value}
          </p>
          {subValue && (
            <p className="text-xs mt-0.5 font-mono-data" style={{ color: 'var(--dark-muted)' }}>
              {subValue}
            </p>
          )}
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accentBg ?? 'rgba(255,255,255,0.06)' }}
        >
          {icon}
        </div>
      </div>

      {children}

      {change && (
        <div className="flex items-center gap-1.5">
          {changePositive ? (
            <TrendingUp size={13} style={{ color: 'var(--success-text)' }} />
          ) : (
            <TrendingDown size={13} style={{ color: 'var(--danger-text)' }} />
          )}
          <span
            className="text-xs font-semibold"
            style={{ color: changePositive ? 'var(--success-text)' : 'var(--danger-text)' }}
          >
            {change}
          </span>
          {changeLabel && (
            <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              {changeLabel}
            </span>
          )}
        </div>
      )}

      {href && (
        <div className="flex items-center gap-1 mt-auto pt-1">
          <span className="text-xs font-medium" style={{ color: 'var(--accent)', opacity: 0.8 }}>View details →</span>
        </div>
      )}
    </div>
  );

  if (href) {
    return (
      <Link href={href} className={`group block ${span2 ? 'col-span-1 md:col-span-2' : ''}`}>
        {cardContent}
      </Link>
    );
  }

  return <div className={span2 ? 'col-span-1 md:col-span-2' : ''}>{cardContent}</div>;
}

// Progress bar for YTD target card
function TargetProgress({ percent }: { percent: number }) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs" style={{ color: 'var(--dark-muted)' }}>
        <span>Progress to $1.176B Target</span>
        <span className="font-semibold tabular-nums" style={{ color: 'var(--accent)' }}>
          {percent.toFixed(1)}%
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${percent}%`,
            background: 'linear-gradient(90deg, #b8860b 0%, #d4a017 60%, #f0c040 100%)',
          }}
        />
      </div>
      <div className="flex justify-between text-xs">
        <span style={{ color: 'var(--dark-muted)' }}>$0</span>
        <span style={{ color: 'var(--dark-muted)' }}>$1.176B</span>
      </div>
    </div>
  );
}

// Grid plan: 6 cards
// Row 1: Hero (col-span-2) + 2 regular = 4-col grid → hero:2 + card + card
// Row 2: 3 regular cards → card + card + card (+ empty fill on 4-col)
// Use grid-cols-4, hero spans 2

export default function KPIBentoGrid() {
  // YTD: $818M collected / $1.176B target = 69.6%
  const ytdPercent = (818 / 1176) * 100;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-3 sm:gap-4">
      {/* Hero: YTD Collections — spans 2 cols */}
      <KPICard
        title="YTD National Collections"
        value="$818.0M"
        subValue="LRD 130.9B equivalent · As of May 28, 2026"
        accentColor="var(--accent)"
        accentBg="rgba(212,160,23,0.15)"
        icon={<DollarSign size={20} style={{ color: 'var(--accent)' }} />}
        change="+17.0%"
        changePositive
        changeLabel="vs FY2025 same period ($699M)"
        span2
        href="/collections"
      >
        <TargetProgress percent={ytdPercent} />
      </KPICard>

      {/* Daily Collection Rate */}
      <KPICard
        title="Daily Collection Rate"
        value="$4.21M"
        subValue="Target: $3.22M/day run-rate"
        accentColor="#4ade80"
        accentBg="rgba(74,222,128,0.1)"
        icon={<TrendingUp size={20} style={{ color: '#4ade80' }} />}
        change="+30.7%"
        changePositive
        changeLabel="above daily target"
        href="/revenue-analytics"
      />

      {/* Compliance Rate */}
      <KPICard
        title="National Compliance Rate"
        value="67.3%"
        subValue="124,891 of 185,600 registered taxpayers"
        accentColor="#60a5fa"
        accentBg="rgba(96,165,250,0.1)"
        icon={<CheckCircle size={20} style={{ color: '#60a5fa' }} />}
        change="+4.2pp"
        changePositive
        changeLabel="vs Q1 2026"
        href="/compliance-engine"
      />

      {/* Active Fraud Alerts — ALERT STATE */}
      <KPICard
        title="Active Fraud Alerts"
        value="47"
        subValue="12 critical · 35 medium priority"
        accentColor="#fca5a5"
        accentBg="rgba(220,38,38,0.12)"
        icon={<AlertTriangle size={20} style={{ color: '#fca5a5' }} />}
        alert
        change="+8"
        changePositive={false}
        changeLabel="new alerts today"
        href="/compliance-engine"
      />

      {/* Delinquency Volume */}
      <KPICard
        title="Total Delinquency"
        value="$94.7M"
        subValue="LRD 15.1B · 8,342 accounts"
        accentColor="#fb923c"
        accentBg="rgba(251,146,60,0.1)"
        icon={<ShieldAlert size={20} style={{ color: '#fb923c' }} />}
        change="-2.1%"
        changePositive
        changeLabel="vs last month (improving)"
        href="/tax-filings"
      />

      {/* New TIN Registrations */}
      <KPICard
        title="New TIN Registrations"
        value="1,847"
        subValue="MTD · Target: 2,000/month"
        accentColor="#a78bfa"
        accentBg="rgba(167,139,250,0.1)"
        icon={<Users size={20} style={{ color: '#a78bfa' }} />}
        change="+23.1%"
        changePositive
        changeLabel="vs April 2026"
        href="/taxpayer-registry"
      />

      {/* Customs Revenue MTD */}
      <KPICard
        title="Customs Revenue MTD"
        value="$38.4M"
        subValue="Target: $41.2M · Freeport + Borders"
        accentColor="#34d399"
        accentBg="rgba(52,211,153,0.1)"
        icon={<Package size={20} style={{ color: '#34d399' }} />}
        change="-6.8%"
        changePositive={false}
        changeLabel="below May target"
        href="/customs-border"
      />
    </div>
  );
}